<template>
  <!--
    Mobile-only site (dedicated subdomain — see libs/mobileSite.js).
    The ONLY page after login there: a slim bar (user, lock, logout), then either
      • "no access"  — user lacks MOBILE_DASHBOARD,
      • the lock screen — view code ("mobile password") required after login and
        again after 5 idle minutes (the dashboard is NOT rendered while locked),
      • the mobile Super Dashboard (TestDashboard.vue, reused untouched).
  -->
  <div class="ms">
    <div class="ms__bar">
      <span class="ms__brand"><i class="fa-solid fa-mobile-screen-button"></i>{{ $t('mobileSite.title') }}</span>
      <span class="ms__user">{{ userFullName }}</span>
      <button type="button" class="ms__btn" :title="$t('mobileSite.darkMode')" @click="toggleDark">
        <i :class="isDark ? 'fa-solid fa-sun' : 'fa-solid fa-moon'"></i>
      </button>
      <button v-if="unlocked && hasAccess" type="button" class="ms__btn" :title="$t('mobileSite.lock')" @click="lock(false)">
        <i class="fa-solid fa-lock"></i>
      </button>
      <button type="button" class="ms__btn" :title="$t('mobileSite.logout')" @click="logout">
        <i class="fa-solid fa-right-from-bracket"></i>
      </button>
    </div>

    <!-- No permission: nothing else to see on this site -->
    <div v-if="!hasAccess" class="ms__gate">
      <div class="ms__card">
        <div class="ms__icon ms__icon--warn"><i class="fa-solid fa-ban"></i></div>
        <p class="ms__text">{{ $t('mobileSite.noAccess') }}</p>
        <button type="button" class="ms__submit" @click="logout">{{ $t('mobileSite.logout') }}</button>
      </div>
    </div>

    <!-- Lock screen -->
    <div v-else-if="!unlocked" class="ms__gate">
      <form class="ms__card" @submit.prevent="submit">
        <div class="ms__icon"><i class="fa-solid fa-lock"></i></div>
        <h2 class="ms__title">{{ $t('mobileSite.enterCode') }}</h2>
        <p v-if="idleLocked" class="ms__text ms__text--idle">{{ $t('mobileSite.idleLocked') }}</p>
        <p v-else class="ms__text">{{ $t('mobileSite.codeHint') }}</p>
        <p v-if="!hasMobilePassword" class="ms__text ms__text--warn">{{ $t('mobileSite.notSet') }}</p>
        <input ref="codeInput" v-model="code" type="password" class="ms__input" autocomplete="off"
               :placeholder="$t('mobileSite.codePlaceholder')" :disabled="verifying || !hasMobilePassword"/>
        <p v-if="error" class="ms__error">{{ error }}</p>
        <button type="submit" class="ms__submit" :disabled="verifying || !code || !hasMobilePassword">
          <b-spinner v-if="verifying" small class="mr-50"/>{{ $t('mobileSite.unlock') }}
        </button>
      </form>
    </div>

    <!-- The dashboard itself — only mounted while unlocked -->
    <div v-else class="ms__content">
      <test-dashboard/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Permissions from '@/model/Permissions'
import { MOBILE_SITE_IDLE_MS } from '@/libs/mobileSite'
import TestDashboard from '@/views/mobile/TestDashboard.vue'

// Survives a page refresh within the idle window (per tab), so a reload does not
// ask for the code again; a lock always clears it.
const LAST_ACTIVITY_KEY = 'mobileSite.lastActivity'
const ACTIVITY_EVENTS = ['touchstart', 'pointerdown', 'mousedown', 'keydown', 'scroll', 'wheel']
const CHECK_EVERY_MS = 10 * 1000

export default {
  name: 'MobileSite',
  components: { TestDashboard },
  data: () => ({
    unlocked: false,
    idleLocked: false,
    code: '',
    error: '',
    verifying: false,
    checkTimer: null,
    lastTouch: 0,
  }),
  computed: {
    ...mapGetters('user', ['user', 'userFullName', 'hasPermission', 'isMultiOrgUser']),
    hasAccess() { return this.hasPermission(Permissions.MOBILE_DASHBOARD) },
    hasMobilePassword() { return !!(this.user && this.user.hasMobilePassword) },
    // Same skin switch as the CRM navbar's DarkToggler (persisted in localStorage).
    isDark() { return this.$store.state.appConfig.layout.skin === 'dark' },
  },
  created() {
    // "Super" (multi-organization view) is ALWAYS on here for eligible users —
    // there is no navbar toggle on the mobile site. Same mechanism as the CRM's
    // Super entry: the flag makes every API call carry the multi-org header.
    // The flag is per site, so the first visit sets it and reloads once, so the
    // profile/organizations are re-fetched in multi-org scope.
    // Without it the session falls back to the ONE organization the host
    // resolves to, and every figure quietly drops the other organizations —
    // which is why the write is checked instead of assumed: a browser that
    // refuses storage (private mode) must not reload forever, and must not
    // leave the page silently showing a narrower scope than the desktop CRM.
    if (this.isMultiOrgUser && this.readMultiOrgFlag() !== 'true') {
      if (this.writeMultiOrgFlag()) {
        window.location.reload()
        return
      }
      console.warn('Mobile site: multi-organization flag could not be stored — data stays scoped to this host\'s organization')
    }
    // Refresh inside the idle window keeps the session unlocked.
    const last = this.readLastActivity()
    this.unlocked = last > 0 && Date.now() - last < MOBILE_SITE_IDLE_MS
    if (!this.unlocked) this.clearLastActivity()
  },
  mounted() {
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, this.onActivity, { passive: true, capture: true }))
    document.addEventListener('visibilitychange', this.checkIdle)
    this.checkTimer = setInterval(this.checkIdle, CHECK_EVERY_MS)
    this.focusInput()
  },
  beforeDestroy() {
    ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, this.onActivity, { capture: true }))
    document.removeEventListener('visibilitychange', this.checkIdle)
    clearInterval(this.checkTimer)
  },
  methods: {
    ...mapActions('user', ['verifyMobilePassword', 'logoutUser']),
    // ---- multi-organization ("Super") flag ----
    // Same localStorage key the CRM navbar's Super toggle uses; it is what makes
    // axios send the x-multiple-organizations header. Both accessors are guarded
    // because a browser can refuse storage outright.
    readMultiOrgFlag() {
      try { return localStorage.getItem('multipleOrganizations') } catch (e) { return null }
    },
    writeMultiOrgFlag() {
      try {
        localStorage.setItem('multipleOrganizations', 'true')
        return localStorage.getItem('multipleOrganizations') === 'true'
      } catch (e) { return false }
    },
    // ---- idle tracking ----
    readLastActivity() {
      try { return Number(sessionStorage.getItem(LAST_ACTIVITY_KEY)) || 0 } catch (e) { return 0 }
    },
    writeLastActivity(ts) {
      try { sessionStorage.setItem(LAST_ACTIVITY_KEY, String(ts)) } catch (e) { /* private mode */ }
    },
    clearLastActivity() {
      try { sessionStorage.removeItem(LAST_ACTIVITY_KEY) } catch (e) { /* ignore */ }
    },
    onActivity() {
      if (!this.unlocked) return
      const now = Date.now()
      if (now - this.lastTouch < 1000) return // throttle storage writes
      this.lastTouch = now
      this.writeLastActivity(now)
    },
    checkIdle() {
      if (!this.unlocked) return
      if (document.hidden) return // judged when the tab comes back
      const last = this.readLastActivity()
      if (!last || Date.now() - last >= MOBILE_SITE_IDLE_MS) this.lock(true)
    },
    // ---- lock / unlock ----
    lock(idle) {
      this.unlocked = false
      this.idleLocked = !!idle
      this.code = ''
      this.error = ''
      this.clearLastActivity()
      this.focusInput()
    },
    async submit() {
      if (this.verifying || !this.code) return
      this.verifying = true
      this.error = ''
      try {
        await this.verifyMobilePassword({ code: this.code })
        this.code = ''
        this.idleLocked = false
        this.lastTouch = Date.now()
        this.writeLastActivity(this.lastTouch)
        this.unlocked = true
      } catch (err) {
        this.error = this.$t('mobileSite.wrongCode')
        this.focusInput()
      } finally {
        this.verifying = false
      }
    },
    focusInput() {
      this.$nextTick(() => { if (this.$refs.codeInput) this.$refs.codeInput.focus() })
    },
    toggleDark() {
      this.$store.commit('appConfig/UPDATE_SKIN', this.isDark ? 'light' : 'dark')
    },
    logout() {
      this.clearLastActivity()
      this.logoutUser().finally(() => this.$router.push('/login'))
    },
  },
}
</script>

<style lang="scss" scoped>
.ms {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f5f6fa;

  &__bar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.9rem;
    padding-top: calc(0.55rem + env(safe-area-inset-top));
    background: #1f2937;
    color: #fff;
  }
  &__brand {
    font-weight: 700;
    font-size: 0.9rem;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    white-space: nowrap;
  }
  &__user {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: right;
    font-size: 0.8rem;
    opacity: 0.8;
  }
  &__btn {
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.12);
    color: #fff;
    font-size: 0.95rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &:active { background: rgba(255, 255, 255, 0.28); }
  }

  &__gate {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem 1rem;
  }
  &__card {
    width: 100%;
    max-width: 380px;
    background: #fff;
    border-radius: 16px;
    padding: 1.75rem 1.5rem;
    box-shadow: 0 12px 34px rgba(31, 41, 55, 0.12);
    text-align: center;
  }
  &__icon {
    width: 56px;
    height: 56px;
    margin: 0 auto 1rem;
    border-radius: 50%;
    background: rgba(0, 123, 255, 0.1);
    color: #007bff;
    font-size: 1.4rem;
    display: flex;
    align-items: center;
    justify-content: center;
    &--warn { background: rgba(234, 84, 85, 0.12); color: #ea5455; }
  }
  &__title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }
  &__text {
    font-size: 0.85rem;
    color: #6b7280;
    margin-bottom: 1rem;
    &--idle { color: #b45309; font-weight: 600; }
    &--warn { color: #ea5455; font-weight: 600; }
  }
  &__input {
    width: 100%;
    font-size: 1.25rem;
    letter-spacing: 0.25em;
    text-align: center;
    padding: 0.7rem 0.9rem;
    border: 1px solid #d1d5db;
    border-radius: 10px;
    outline: none;
    &:focus { border-color: #007bff; box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.15); }
    &:disabled { background: #f3f4f6; }
  }
  &__error {
    color: #ea5455;
    font-size: 0.85rem;
    font-weight: 600;
    margin: 0.6rem 0 0;
  }
  &__submit {
    width: 100%;
    margin-top: 1rem;
    padding: 0.75rem;
    border: 0;
    border-radius: 10px;
    background: #007bff;
    color: #fff;
    font-weight: 700;
    font-size: 1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    &:disabled { opacity: 0.55; }
  }

  // Standalone site: no app shell around the dashboard, so give it the
  // breathing room the CRM content area normally provides.
  &__content {
    flex: 1;
    padding: 0.75rem 1rem 1.5rem;
    padding-left: calc(1rem + env(safe-area-inset-left));
    padding-right: calc(1rem + env(safe-area-inset-right));
  }
}

.dark-layout .ms {
  background: #161d31;
  &__card { background: #283046; box-shadow: 0 12px 34px rgba(0, 0, 0, 0.35); }
  &__title { color: #d0d2d6; }
  &__text { color: #b4b7bd; }
  &__input { background: #161d31; border-color: #3b4253; color: #d0d2d6; }
}
</style>
